import { Component, inject } from '@angular/core';
import { HeaderComponent } from '../../atoms/header/header.component';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { IndicatorService } from '../../../services/indicator/indicator.service';
import { ToastModule } from 'primeng/toast';
import {
  ImportIndicator,
  ImportValidatorService,
} from '../../../services/indicator/import-validator.service';
import { CardModule } from 'primeng/card';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { AlertStyleDirective } from '../../../directives/alert-style.directive';
import { IndicatorImportListComponent } from '../indicator-import-list/indicator-import-list.component';
import { SidebarModule } from 'primeng/sidebar';
import { DividerModule } from 'primeng/divider';
import { MaskComponent } from '../mask/mask.component';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { IndicatorPickListComponent } from '../../molecules/indicator-pick-list/indicator-pick-list.component';
import { IndicatorExportConverterService } from '../../../services/indicator-export-converter.service';
import { DownloadTemplateComponent } from '../../atoms/download-template/download-template.component';
import { Severity } from '../../../models/severity';
import { ImportPreviewBoxComponent } from '../../molecules/import-preview-box/import-preview-box.component';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { CategoryStore } from '../../../stores/category/category.state';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-import-export-indicators',
  standalone: true,
  imports: [
    HeaderComponent,
    FileUploadModule,
    ToastModule,
    CardModule,
    NgIf,
    AlertStyleDirective,
    IndicatorImportListComponent,
    SidebarModule,
    DividerModule,
    MaskComponent,
    NgClass,
    IndicatorPickListComponent,
    DownloadTemplateComponent,
    ImportPreviewBoxComponent,
    NgForOf,
    AlertComponent,
    RouterLink,
  ],
  templateUrl: './import-export-indicators.component.html',
  styleUrl: './import-export-indicators.component.scss',
})
export class ImportExportIndicatorsComponent {
  readonly categoryStore = inject(CategoryStore);
  readonly indicatorStore = inject(IndicatorStore);
  indicatorsToImport: ImportIndicator[] = [];
  indicatorsToShowInOverview: ImportIndicator[] = [];
  icon = '';
  activeExportSelection = false;

  constructor(
    protected indicatorService: IndicatorService,
    private messageService: MessageService,
    private importValidationService: ImportValidatorService,
    private indicatorExportConverterService: IndicatorExportConverterService
  ) {}

  validationPreviewSeverities: Severity[] = [
    'success',
    'info',
    'warning',
    'error',
  ];

  /**
   * Resets the import state by clearing all relevant properties to their initial values.
   * It empties the lists of indicators to import and to show in the overview, clears the icon,
   * and sets the export selection state to inactive.
   *
   * @return {void} No return value.
   */
  resetImport(): void {
    this.indicatorsToImport = [];
    this.indicatorsToShowInOverview = [];
    this.icon = '';
    this.activeExportSelection = false;
  }

  /**
   * Handles the upload event by validating and mapping the uploaded data.
   * Displays a success or error message based on the validation result.
   *
   * @param {any} event - The upload event containing the uploaded data.
   * @return {void} - Does not return a value.
   */
  onUpload(event: any): void {
    this.resetImport();
    try {
      this.indicatorsToImport = this.importValidationService.validateAndMap(
        event.originalEvent.body
      );
      this.messageService.add({
        severity: 'info',
        summary: 'Upload erfolgreich!',
      });
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Upload fehlgeschlagen!',
        life: 10000,
        detail:
          'Überprüfen Sie Ihre Vorlage und versuchen Sie es erneut. Sind Sie im richtigen Importbereich? Dieser Bereich steht nur für Indikatoren zur Verfügung!',
      });
    }
  }

  /**
   * Imports indicators by filtering out parent indicators and updating the indicator store with the selected items.
   * After the import operation, it resets the import process.
   *
   * @return {void} Does not return a value.
   */
  importIndicators(): void {
    this.indicatorStore.updateMultiple({
      items: this.indicatorsToImport.filter(indicator => {
        return !indicator.parent;
      }),
      message: {
        severity: 'info',
        summary: 'Import erfolgreich!',
      },
    });

    this.resetImport();
  }

  /**
   * Retrieves the count of items where the validation state matches the specified key.
   *
   * @param {string} key - The key to match against the validation state of the items.
   * @return {number} The count of items with the matching validation state.
   */
  getCountByKey(key: string): number {
    return this.indicatorsToImport.filter(item => {
      return item.validation.state === key;
    }).length;
  }

  /**
   * Updates the list of items to be shown based on the provided state.
   * Filters the existing indicators to include only those matching the specified validation state
   * and updates the internal state accordingly. Additionally, sets the icon corresponding to the state.
   *
   * @param {Severity} state - The state used to filter the indicators and define the items to show.
   * @return {void} Does not return a value.
   */
  setItemsToShow(state: Severity): void {
    this.indicatorsToShowInOverview = this.indicatorsToImport.filter(item => {
      return item.validation.state === state;
    });
    this.setIconByState(state);
  }

  /**
   * Sets the icon property of the object based on the provided state.
   * Updates the icon to a specific value corresponding to predefined states.
   *
   * @param {string} state - The state based on which the icon is determined.
   *                         Acceptable values are 'new', 'update', 'warning', 'error', or any other string for the default case.
   * @return {void}
   */
  setIconByState(state: string): void {
    switch (state) {
      case 'new':
        this.icon = 'fas fa-circle-plus';
        break;
      case 'update':
        this.icon = 'fas fa-save';
        break;
      case 'warning':
        this.icon = 'fas fa-exclamation-triangle';
        break;
      case 'error':
        this.icon = 'fas fa-exclamation-circle';
        break;
      default:
        this.icon = '';
    }
  }

  /**
   * close overview
   */
  closeOverview(): void {
    this.indicatorsToShowInOverview = [];
    this.icon = '';
  }

  /**
   * close export-pick-list
   */
  closeExportSelection(): void {
    this.activeExportSelection = false;
  }

  /**
   * Exports all indicators by preparing them for export and converting them to an XLSX format.
   *
   * @return {void} Does not return a value.
   */
  exportAll(): void {
    this.indicatorStore.convertToXlsx({
      indicatorRows:
        this.indicatorExportConverterService.prepareIndicatorsForExport([
          ...this.indicatorStore.asArray(),
        ]),
    });
  }

  /**
   * Exports data by converting the selected indicators to an XLSX format.
   * It retrieves the selected indicators, prepares them for export, and triggers the conversion process.
   * After the export process is initiated, it closes the export selection interface.
   *
   * @return {void} Does not return a value.
   */
  export(): void {
    this.indicatorStore.convertToXlsx({
      indicatorRows:
        this.indicatorExportConverterService.prepareIndicatorsForExport([
          ...this.indicatorStore.selection(),
        ]),
    });
    this.closeExportSelection();
  }
}
